import { type CSSProperties, type FC, type PropsWithChildren, useCallback, useMemo } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import type { FlagCountry } from "@stubhub/broadway-web/icons/flags";
import {
  EventGridListItem,
  EventGridListItemEventState,
  EventGridListItemEventType,
  EventGridListItemKind,
  EventGridListItemVariant,
} from "@stubhub/ensemble-discovery";
import type { EventListGridItem } from "src/http/types";
import { useInView } from "src/shared/hooks/useInView";
import { CALENDAR_CSS_VARIABLES, calendarTilePortion } from "./styles";

const inViewSentinel = css({
  width: "primitives.0",
  height: "primitives.0",
  position: "absolute",
});

const tileWrapper = css({
  display: "contents",
});

// Ensemble's container layout overrides (layout/padding resets, hover treatment)
// are now folded into the calendarTilePortion SVA's base `root` slot.

interface Props {
  /** URL to navigate to when the portion is clicked (used when `eventState === Active`) */
  eventURL?: string;
  /** Primary color for styling the portion's theme */
  primaryColor?: CSSProperties["color"];
  /** Click event handler (called with `null` for parity with the legacy API) */
  onClick?: (event: null) => void;
  /** Mouse enter event handler (called with `null` for parity with the legacy API) */
  onMouseEnter?: (event: null) => void;
  /** Fired once when the portion first scrolls into view (75% visible) */
  onInView?: (() => void) | undefined;
  /** Accessibility label for screen readers */
  ariaLabel?: string | undefined;
  /**
   * Event for EventGridListItem context — required so tag children can consume
   * `useEventGridListItemContext()`.
   */
  event: EventListGridItem;
  /**
   * Drives interactivity: `Active` → Ensemble renders an `<a>`, otherwise `<button>`.
   * Defaults to `Active`.
   */
  eventState?: EventGridListItemEventState | undefined;
}

/**
 * Generic base component for rendering a portion of a calendar date card when multiple events occur on the same day.
 *
 * Renders via Ensemble's `EventGridListItem` so children (merch tags, ctas) can consume
 * `useEventGridListItemContext()` for tooltip + qualifier rendering.
 */
export const BaseDateCardPortion: FC<PropsWithChildren<Props>> = ({
  children,
  eventURL,
  primaryColor,
  onClick,
  onMouseEnter,
  onInView,
  ariaLabel,
  event,
  eventState = EventGridListItemEventState.Active,
}) => {
  const hasPrimaryColor = !!primaryColor;
  // Slot recipes type slot classes as `string | undefined`; every slot here
  // has a base style so we coerce to `string` with `?? ""` for each slot.
  const rawPortionStyles = useMemo(
    () => calendarTilePortion({ primaryColor: hasPrimaryColor ? "applied" : "default" }),
    [hasPrimaryColor],
  );
  const portionStyles = useMemo(
    () => ({
      root: rawPortionStyles.root ?? "",
      topLine: rawPortionStyles.topLine ?? "",
    }),
    [rawPortionStyles],
  );

  const containerStyle: CSSProperties = useMemo(
    () => ({
      [CALENDAR_CSS_VARIABLES.primaryColor]: primaryColor ?? "transparent",
    }),
    [primaryColor],
  );

  const { ref: inViewRef } = useInView({
    triggerOnce: true,
    threshold: 0.75,
    onChange: (inView: boolean) => {
      if (inView) onInView?.();
    },
  });

  const contents = useMemo(
    () => (
      <>
        <div className={portionStyles.topLine} role="presentation" />
        {children}
        {!!onInView && <div className={inViewSentinel} ref={inViewRef} aria-hidden />}
      </>
    ),
    [portionStyles.topLine, children, onInView, inViewRef],
  );

  const handleClick = useCallback(() => {
    onClick?.(null);
  }, [onClick]);

  const handleMouseEnter = useCallback(() => {
    onMouseEnter?.(null);
  }, [onMouseEnter]);

  return (
    <span className={tileWrapper} style={containerStyle}>
      <EventGridListItem
        href={eventURL ?? "#"}
        target="_blank"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        kind={EventGridListItemKind.Neutral}
        variant={EventGridListItemVariant.Ghost}
        eventState={eventState}
        eventType={event.isParkingEvent ? EventGridListItemEventType.Parking : EventGridListItemEventType.Event}
        eventTitle={event.name}
        venueName={event.venueName ?? ""}
        venueLocation={[event.venueCity, event.venueStateProvince].filter(Boolean).join(", ")}
        venueCountryCode={(event.countryCode ?? "") as FlagCountry}
        startTimeMillis={event.eventMetadata?.common?.eventStartDateTime}
        endTimeMillis={event.eventMetadata?.common?.eventEndDateTime ?? undefined}
        venueTimeZoneOffsetMillis={event.eventMetadata?.common?.venueTimeZoneOffset}
        passLength={event.eventMetadata?.common?.passLength ?? undefined}
        isDateConfirmed={event.isDateConfirmed}
        isTimeConfirmed={event.isTimeConfirmed}
        classes={{ container: portionStyles.root }}
        aria-label={ariaLabel}
      >
        {contents}
      </EventGridListItem>
    </span>
  );
};
